import { head } from 'es-toolkit'
import { ChevronDown } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { useState } from 'react'

import { compactFormControlClass } from '@/components/ui/form'
import { useFormControlProps } from '@/components/ui/form-field-context'
import { cn } from '@/lib/utils'

// 使い方のルール: docs/develop/client_ui/components/form.md

//
// antd Select(mode="multiple") 相当のチェックボックス付きドロップダウン。
// v0-ui-mock のミルシートステータス絞り込みUIの共通部品化（2026/08/26 FB）。
// ネイティブ <select multiple>（リストボックス）は複数選択に ctrl/cmd+クリックが必要で
// 操作方法が伝わらないため、クリックでトグルできるチェックボックス式に統一する
//

export type MultiSelectOption = { value: string; label: string }

export function MultiSelect({
  id,
  label,
  values,
  options,
  placeholder = 'すべて',
  onChange,
  testId,
  dropdownHeader,
  dropdownFooter,
  loading = false,
  getValueLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  id: string
  // ドロップダウン（listbox）のアクセシブルな名前に使う（トリガーの label 要素とは別に必要）
  label: string
  values: string[]
  options: MultiSelectOption[]
  placeholder?: string
  onChange: (next: string[]) => void
  testId?: string
  dropdownHeader?: ReactNode
  dropdownFooter?: ReactNode
  loading?: boolean
  getValueLabel?: (value: string) => string
  'aria-invalid'?: ComponentProps<'button'>['aria-invalid']
  'aria-describedby'?: string
}) {
  const control = useFormControlProps({ id, 'aria-invalid': ariaInvalid, 'aria-describedby': ariaDescribedBy })
  const [open, setOpen] = useState(false)

  const labelByValue = new Map(options.map((option) => [option.value, option.label]))
  const first = head(values)
  const summary =
    values.length === 0
      ? placeholder
      : values.length === 1 && first !== undefined
        ? (labelByValue.get(first) ?? getValueLabel?.(first) ?? first)
        : `${values.length}件を選択`

  const toggle = (value: string) => {
    onChange(values.includes(value) ? values.filter((x) => x !== value) : [...values, value])
  }

  return (
    <div className="relative">
      <button
        id={control.id ?? id}
        type="button"
        onClick={() => {
          setOpen((current) => !current)
        }}
        className={cn(compactFormControlClass, 'flex items-center justify-between gap-2 text-left')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={control['aria-invalid']}
        aria-describedby={control['aria-describedby']}
        data-testid={testId}
      >
        <span className={cn('truncate', values.length === 0 && 'text-muted-foreground')}>{summary}</span>
        <ChevronDown
          className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <>
          {/* 外側クリックで閉じるための透明な backdrop（orders-table の表示列メニューと同じ方式） */}
          <div
            className="fixed inset-0 z-10"
            aria-hidden="true"
            onClick={() => {
              setOpen(false)
            }}
          />
          <div
            role="listbox"
            aria-label={label}
            aria-multiselectable="true"
            className="absolute z-20 mt-1 max-h-72 w-full min-w-fit overflow-y-auto rounded-lg border border-border bg-card p-1.5 shadow-lg"
          >
            {dropdownHeader}
            {loading && (
              <p role="status" className="px-2.5 py-2 text-sm text-muted-foreground">
                読み込み中
              </p>
            )}
            {!loading && options.length === 0 && (
              <p className="px-2.5 py-2 text-sm text-muted-foreground">選択肢がありません</p>
            )}
            {!loading &&
              options.map((option) => {
                const checked = values.includes(option.value)
                return (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium whitespace-nowrap text-foreground hover:bg-secondary"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        toggle(option.value)
                      }}
                      className="size-4 accent-primary"
                      data-testid={testId === undefined ? undefined : `${testId}-option-${option.value}`}
                    />
                    {option.label}
                  </label>
                )
              })}
            {dropdownFooter}
          </div>
        </>
      )}
    </div>
  )
}
